import { useSelector } from 'react-redux';
import { Tooltip } from '@material-ui/core';
import copy from 'copy-to-clipboard';
import styled from 'styled-components';
import groupOfDudes from 'images/characters/group-of-dudes.svg';
import twoDudes from 'images/characters/two-dudes.svg';
import { Flex, StyledCode } from 'components/base/layout-components';
import Text from 'components/base/Text';
import Icon from 'components/base/Icon';
import Table from 'components/base/Table';
import { getColor } from 'common/constants/_colors';
import {
  getTableColumns,
  getTableData,
  createTeamData,
  TableWrapper
} from '../../ChatTools/common';
import { SPACES, RADIUS } from 'common/constants/sizes';
import MsTeamsConnectButton from './MsTeamsConnectButton';
import { isTeamLicenseSelector } from 'state/selectors/license';
import { LBButton } from '@linearb/buzz';

const StyledContainerWrapper = ({ children, ...rest }) => (
  <Flex
    backgroundColor={getColor('zircon')}
    border={`1px solid ${getColor('titanWhite')}`}
    flex={1}
    direction='row'
    pb='normal'
    {...rest}
  >
    {children}
  </Flex>
);

const StyledCodeWrapper = ({ displayText = '', children, ...rest }) => (
  <Flex
    backgroundColor={getColor('titanWhite')}
    margin='x-small'
    direction='row'
    items='center'
    borderRadius={RADIUS.small}
    pt='x-x-x-small'
    pr='x-small'
    pb='x-x-x-small'
    pl='x-small'
    {...rest}
  >
    <StyledCode
      backgroundColor='titanWhite'
      margin='0 0 0 0'
      padding='0 0.5rem 0 0'
    >
      <Text size='medium' color='chambray'>
        {displayText}
      </Text>
    </StyledCode>
    {children}
  </Flex>
);

const CopyButton = ({ data = '' }) => (
  <LBButton variant='ghost' size='icon' onClick={() => copy(data)}>
    <Icon name='Clipboard' />
  </LBButton>
);

const StyledImg = styled.img`
  margin: 1.5rem;
  margin-right: 0.7rem;
  margin-bottom: 0;
`;

const CompanySettings = ({
  clientId = '',
  hasTeams,
  isOrgSettings = true,
  currentTeams,
  settings,
  currentTeam,
  uiState,
  msTeamsSensorAuthorization,
  history,
  selectTeamId,
  toggleNotificationsForTeam,
  unlinkTeam
}) => {
  const isTeamLicense = useSelector(isTeamLicenseSelector);
  const createTeamDataWrapper = (team, teamSettings) => {
    if (!teamSettings) {
      return null;
    }
    const { msTeamsChannel = null } = teamSettings || {};
    if (!msTeamsChannel || !msTeamsChannel.isConfigurationComplete) {
      return null;
    }
    return createTeamData(
      team,
      teamSettings,
      msTeamsSensorAuthorization,
      settings,
      selectTeamId,
      history,
      msTeamsChannel,
      `${msTeamsChannel.msteamsTeamName}/${msTeamsChannel.msteamsChannelName}`,
      false,
      toggleNotificationsForTeam,
      null,
      null,
      unlinkTeam,
      isTeamLicense
    );
  };
  const tableColumns = getTableColumns('TEAM / CHANNEL');
  tableColumns.push({
    name: 'UNLINK',
    dataPoint: 'unlinkNotifications',
    tableCellProps: {
      tableCellProps: { style: { borderRight: 0, borderLeft: 0 } }
    }
  });
  const tableData = getTableData({
    hasTeams,
    isOrgSettings,
    currentTeams,
    settings,
    currentTeam,
    createTeamData: createTeamDataWrapper,
    uiState
  });
  return (
    <Flex direction='column'>
      <Text size='x-medium' pb={SPACES.small}>
        {tableData.length} Connected teams
      </Text>
      <Flex
        direction='row'
        items='stretch'
        justify='space-between'
        width='100%'
      >
        <StyledContainerWrapper pr='small'>
          <StyledImg src={groupOfDudes} alt='' />
          <Flex direction='column' width='100%' justify='center' ml='x-small'>
            <Text mt={SPACES.medium} size='x-large'>
              Add a team
            </Text>
            <div className='flex flex-row items-center'>
              <Text color='shuttleGray' size='x-medium'>
                Type:
              </Text>
              <StyledCodeWrapper displayText='link team'>
                <CopyButton data={'link team'} />
              </StyledCodeWrapper>
              <Text color='shuttleGray' mr={SPACES.small} size='x-medium'>
                into a private chat with WorkerB to connect your team
              </Text>
              <MsTeamsConnectButton
                ButtonText='Open Chat'
                clientId={clientId}
              />
            </div>
          </Flex>
        </StyledContainerWrapper>
        <StyledContainerWrapper ml='small'>
          <StyledImg src={twoDudes} alt='' />
          <Flex direction='column' width='100%' justify='center' ml='x-small'>
            <Text mt={SPACES.medium} size='x-large'>
              Invite your friends
            </Text>
            <Flex direction='row' items='center'>
              <Text color='shuttleGray' size='x-medium'>
                Share this link to invite your friends:
              </Text>
              <Tooltip title={clientId}>
                <div>
                  <StyledCodeWrapper displayText='https://teams.microsoft.com/l/app/'>
                    <CopyButton data={clientId} />
                  </StyledCodeWrapper>
                </div>
              </Tooltip>
            </Flex>
          </Flex>
        </StyledContainerWrapper>
      </Flex>
      <TableWrapper>
        <Table columns={tableColumns} data={tableData} />
      </TableWrapper>
    </Flex>
  );
};

export default CompanySettings;
